import React, { useCallback, useMemo } from 'react';

import { PlatePrice } from '@paragon/contract-library';

import { editPlatePrices } from '../actions/platePriceActions';
import { formatDateTime, removeUnderscores } from './formattingUtilities';
import { useAppDispatch, useAppSelector } from './hooks';
import { InsertPlatePrice } from './InsertPlatePrice';
import { ColumnData, PriceGrid } from './price-grid/PriceGrid';
import { parsePriceEditValue } from './price-grid/priceGridUtilities';

export function PlatePriceGrid() {
  const dispatch = useAppDispatch();
  const platePrices = useAppSelector((state) => state.platePrices);
  const plateTypePropertiesList = useAppSelector((state) => state.plateTypeProperties);

  const columns = useMemo(
    (): ColumnData<PlatePrice>[] => [
      {
        key: 'plateType',
        title: 'Type',
        width: 115,
        getCellValue: (row) => row.plateType,
        filterType: 'select',
        filterLabel: 'All types',
      },
      {
        key: 'plateManufacturer',
        title: 'Plate Manufacturer',
        width: 200,
        getCellValue: (row) =>
          removeUnderscores(
            plateTypePropertiesList.find((plateTypeProperties) => plateTypeProperties.plateType === row.plateType)
              ?.plateManufacturer ?? 'Manufacturer unavailable',
          ),
        filterType: 'select',
        filterLabel: 'All manufacturers',
        groupable: plateTypePropertiesList.length > 0,
      },
      {
        key: 'pricePerPound',
        title: 'Price per Pound',
        width: 170,
        getCellValue: (row) => `$${row.pricePerPound.toFixed(2)}`,
        getUpdatedRowWithEdit: (row, newValue) => ({ ...row, pricePerPound: parsePriceEditValue(newValue) }),
        getEditValue: (row) => row.pricePerPound.toFixed(2),
        editStartAdornment: '$',
        validateEditValue: (newValue) => {
          const parsedValue = Number(newValue);
          if (newValue.trim() === '' || !Number.isFinite(parsedValue) || parsedValue < 0) {
            return 'Enter a non-negative price.';
          }
          return undefined;
        },
        sortByMapping: (row) => row.pricePerPound,
        filterType: 'range',
        rangeFilterLabels: { min: 'Min price', max: 'Max price' },
      },
      {
        key: 'priceDateTime',
        title: 'Last Updated',
        width: 200,
        getCellValue: (row) => formatDateTime(row.priceDateTime),
        sortByMapping: (row) => Date.parse(row.priceDateTime),
      },
      {
        key: 'notes',
        title: 'Notes',
        width: 200,
        getCellValue: (row) => row.notes ?? '',
        getUpdatedRowWithEdit: (row, newValue) => ({ ...row, notes: newValue === '' ? null : newValue }),
        filterType: 'text',
        filterLabel: 'Search notes',
      },
    ],
    [plateTypePropertiesList],
  );

  const commitRowEdits = useCallback(
    (editedRows: readonly PlatePrice[]) => {
      return dispatch(editPlatePrices(editedRows));
    },
    [dispatch],
  );

  return (
    <PriceGrid
      rows={Object.values(platePrices)}
      columns={columns}
      commitRowEdits={commitRowEdits}
      insertBar={(onClose, onHeightChange) => <InsertPlatePrice onClose={onClose} onHeightChange={onHeightChange} />}
      defaultSorting={[{ columnKey: 'plateType', direction: 'asc' }]}
    />
  );
}
